import { useEffect, useRef, useState } from 'react';

interface TocHeading {
  id: string;
  text: string;
  level: 2 | 3;
}

interface TableOfContentsProps {
  /** ref to the <article> element whose headings we index */
  articleRef: React.RefObject<HTMLElement | null>;
  /** total estimated reading time in minutes */
  readingTime?: number;
}

export function TableOfContents({
  articleRef,
  readingTime,
}: TableOfContentsProps) {
  const [headings, setHeadings] = useState<TocHeading[]>([]);
  const [activeId, setActiveId] = useState<string>('');
  const [scrollProgress, setScrollProgress] = useState(0);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // ── 1. Collect headings — wait for MDX content to land in the DOM ──────────
  //
  // The article ref is stable from the first render, but the MDX component
  // inside <Suspense> may not have mounted yet when this effect first runs.
  // We use a MutationObserver to watch for child additions and re-scan
  // whenever the DOM changes, stopping as soon as we find at least one heading.
  useEffect(() => {
    const article = articleRef.current;
    if (!article) return;

    function collect() {
      const els = Array.from(
        article!.querySelectorAll<HTMLHeadingElement>('h2, h3'),
      );
      return els
        .filter((el) => el.id)
        .map((el) => ({
          id: el.id,
          text: el.textContent ?? '',
          level: Number(el.tagName[1]) as 2 | 3,
        }));
    }

    // Try immediately — works when content is already in the DOM (e.g. cached)
    const initial = collect();
    if (initial.length > 0) {
      setHeadings(initial);
      return;
    }

    // Otherwise watch for DOM mutations until headings appear
    const observer = new MutationObserver(() => {
      const found = collect();
      if (found.length > 0) {
        setHeadings(found);
        observer.disconnect();
      }
    });

    observer.observe(article, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [articleRef]);

  // ── 2. Scroll-spy: track active heading via IntersectionObserver ──────────
  useEffect(() => {
    if (headings.length === 0) return;

    observerRef.current?.disconnect();

    // Keep a map of which headings are currently visible
    const visible = new Map<string, number>(); // id → intersectionRatio

    observerRef.current = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            visible.set(entry.target.id, entry.intersectionRatio);
          } else {
            visible.delete(entry.target.id);
          }
        }

        if (visible.size > 0) {
          // Pick the heading with the highest ratio (most visible)
          let bestId = '';
          let bestRatio = -1;
          for (const [id, ratio] of visible) {
            if (ratio > bestRatio) {
              bestRatio = ratio;
              bestId = id;
            }
          }
          setActiveId(bestId);
        }
        // If nothing is visible, keep the last known active heading —
        // this keeps the ToC from flickering as you scroll between sections.
      },
      {
        // Fire when the heading crosses the top third of the viewport
        rootMargin: '-10% 0px -70% 0px',
        threshold: [0, 0.5, 1],
      },
    );

    for (const { id } of headings) {
      const el = document.getElementById(id);
      if (el) observerRef.current.observe(el);
    }

    return () => observerRef.current?.disconnect();
  }, [headings]);

  // ── 3. Article scroll progress ────────────────────────────────────────────
  useEffect(() => {
    const handleScroll = () => {
      const article = articleRef.current;
      if (!article) return;

      const { top, height } = article.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      // progress 0 when article top is at viewport bottom, 1 when article
      // bottom is at viewport top
      const scrolled = viewportHeight - top;
      const total = height + viewportHeight;
      const p = Math.min(1, Math.max(0, scrolled / total));
      setScrollProgress(p);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // run once on mount
    return () => window.removeEventListener('scroll', handleScroll);
  }, [articleRef]);

  if (headings.length === 0) return null;

  const minutesRead =
    readingTime != null ? Math.round(scrollProgress * readingTime) : null;
  const progressPct = Math.round(scrollProgress * 100);

  return (
    <nav aria-label="Table of contents" className="flex min-h-0 flex-1 flex-col gap-4">
      {/* Progress header */}
      {readingTime != null && (
        <div className="flex-none space-y-1.5">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-xs font-medium text-foreground/70">
              {minutesRead != null && minutesRead > 0
                ? `${minutesRead} min read`
                : 'Just started'}
            </span>
            <span className="text-xs text-muted-foreground">
              of {readingTime} min
            </span>
          </div>
          {/* Progress bar */}
          <div
            className="h-0.5 w-full overflow-hidden rounded-full bg-border"
            role="progressbar"
            aria-valuenow={progressPct}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`Reading progress: ${progressPct}%`}
          >
            <div
              className="h-full rounded-full bg-primary transition-all duration-150 ease-out"
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>
      )}

      {/* Heading list */}
      <ul className="overflow-y-auto space-y-0.5">
        {headings.map(({ id, text, level }) => {
          const isActive = id === activeId;
          return (
            <li key={id}>
              <button
                type="button"
                onClick={() => {
                  document
                    .getElementById(id)
                    ?.scrollIntoView({ behavior: 'smooth' });
                  window.history.pushState(null, '', `#${id}`);
                }}
                className={[
                  'block w-full truncate rounded py-1 text-left text-xs leading-snug transition-colors',
                  level === 3 ? 'pl-3' : 'pl-0',
                  isActive
                    ? 'font-medium text-foreground'
                    : 'text-muted-foreground hover:text-foreground/80',
                ].join(' ')}
                title={text}
              >
                {/* Active indicator dot */}
                <span className="inline-flex items-center gap-1.5">
                  <span
                    className={[
                      'inline-block h-1 w-1 flex-none rounded-full transition-all duration-150',
                      isActive ? 'scale-125 bg-primary' : 'bg-transparent',
                    ].join(' ')}
                    aria-hidden
                  />
                  {text}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
